/**
 * The grown-up's screen: children, codes and devices.
 *
 * Reached from the sign-in screen rather than from inside the app, because that
 * is where a parent already is — the first thing anyone does on a new iPad is
 * prove the device, and this is the screen that gives them something to prove
 * it with. It also means a child never taps through to it by accident: they
 * only ever see the app *after* the device is proven, and this lives before
 * that.
 *
 * A grown-up signs in with a **real password**
 * ([D-043](../../../docs/DECISIONS.md#d-043)), never a PIN and never the
 * profile picker. The token that comes back is a different kind from a child's
 * and cannot be used as one.
 *
 * **The device code is shown once.** It is stored hashed, so no screen can show
 * it again — which is the point, and which is why this one says so plainly and
 * keeps it on screen until the grown-up dismisses it.
 */

import '@capytalk/tokens/blocks/settings.css';

import {
  type FormEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import Animal from './Animal.js';
import { type Client, createClient, type Trouble, troubleFrom } from './api.js';
import Dashboard from './Dashboard.js';
import SpeechSetup from './SpeechSetup.js';
import { flagFor } from './language.js';

/**
 * The animal a new child starts as. Five, taken in turn so siblings do not all
 * arrive as foxes; the child picks their own the first time they want to
 * ([D-158](../../../docs/DECISIONS.md#d-158)).
 */
const SPECIES = ['fox', 'otter', 'bear', 'rabbit', 'owl'] as const;

type Children = Awaited<ReturnType<Client['owner']['children']['query']>>;
type Devices = Awaited<ReturnType<Client['owner']['devices']['query']>>;
type Languages = Awaited<ReturnType<Client['owner']['languages']['query']>>;

/**
 * The two rules a new account has to meet, said before they are broken.
 *
 * The same words appear under the fields and in the message when one is
 * broken, and each names its field: "at least three characters" under the
 * password box read as a password rule, which it was not.
 */
const USERNAME_RULE =
  '3 to 32 letters (a–z) or numbers. A dot, dash or underscore can go in the middle. No spaces, no @, and no æ, ø or å.';
const PASSWORD_RULE =
  'at least 12 characters. A short sentence is easiest to remember.';

/** The messages the server's own problem codes turn into. */
const SAYS: Readonly<Record<string, string>> = {
  'username-taken': 'That name is taken. Try another one.',
  'username-shape': `User name: ${USERNAME_RULE}`,
  'password-short': `Password: ${PASSWORD_RULE}`,
  'pin-shape': 'A code is 4 to 6 digits.',
  'pin-repeated':
    'Not four of the same digit: that is the first thing a sibling tries.',
  'pin-sequence': 'Not 1234: that is the second thing a sibling tries.',
  'name-mismatch': 'That is not their name, so nothing was deleted.',
};

/**
 * What is said when the server named no problem this screen knows.
 *
 * Named once because it is also the *test* for "nothing worth saying": a refused
 * sign-in is deliberately indistinguishable from an unknown name, so it falls
 * through to the general trouble note instead of this.
 */
const UNNAMED = 'That did not work. Try again.';

/** A server message that names a known problem, or a general apology. */
function problemFrom(error: unknown): string {
  const message =
    typeof error === 'object' && error !== null && 'message' in error
      ? String(error.message)
      : '';
  return SAYS[message] ?? UNNAMED;
}

/** "1 child" reads wrong as "1 children", and a parent notices. */
const childCount = (n: number): string =>
  n === 1 ? '1 child' : `${String(n)} children`;

function TroubleNote({ trouble }: { readonly trouble: Trouble }) {
  const says: Record<Trouble['kind'], string> = {
    rejected: 'That did not work. Check the name and the password.',
    locked: 'Too many tries. Wait a little, then try again.',
    missing: 'That is not here any more.',
    offline: 'No connection. Is the wi-fi on?',
    unknown: 'Something went wrong. Try again.',
  };
  return (
    <p className="sub" role="alert" style={{ marginTop: 10 }}>
      {says[trouble.kind]}
    </p>
  );
}

// --- signing in -------------------------------------------------------------

export function SignInOrUp({
  client,
  onSignedIn,
  onLeave,
}: {
  readonly client: Client;
  readonly onSignedIn: (token: string) => void;
  /** Where "Back" goes. Absent on the first screen a device shows: there is no back. */
  readonly onLeave?: () => void;
}) {
  const [making, setMaking] = useState(false);
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [trouble, setTrouble] = useState<Trouble | null>(null);
  const [problem, setProblem] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function submit(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    setTrouble(null);
    setProblem(null);
    try {
      const call = making ? client.owner.register : client.owner.start;
      const { token } = await call.mutate({ username, password });
      onSignedIn(token);
    } catch (error) {
      // A named problem — a taken username, a short password — is worth saying.
      // A refused sign-in is not: it is the same answer for a wrong password
      // and an unknown name, on purpose.
      const named = problemFrom(error);
      if (named === UNNAMED) setTrouble(troubleFrom(error));
      else setProblem(named);
    } finally {
      setBusy(false);
    }
  }

  return (
    <form
      className="pad"
      onSubmit={(event) => {
        void submit(event);
      }}
    >
      <p className="kicker">For grown-ups</p>
      <h2>{making ? 'Make an account' : 'Sign in'}</h2>
      <p className="sub">
        We never ask for an email address — not yours, and not your child’s.
      </p>

      <label
        className="tiny"
        htmlFor="owner-username"
        style={{ display: 'block', marginTop: 14 }}
      >
        User name
      </label>
      <input
        id="owner-username"
        className="chip"
        style={{ width: '100%', marginTop: 4 }}
        autoComplete="username"
        aria-describedby={making ? 'owner-username-rule' : undefined}
        value={username}
        onChange={(e) => {
          setUsername(e.target.value);
        }}
      />
      {making && (
        <p id="owner-username-rule" className="tiny" style={{ marginTop: 4 }}>
          {USERNAME_RULE}
        </p>
      )}

      <label
        className="tiny"
        htmlFor="owner-password"
        style={{ display: 'block', marginTop: 10 }}
      >
        Password
      </label>
      <input
        id="owner-password"
        className="chip"
        style={{ width: '100%', marginTop: 4 }}
        type="password"
        autoComplete={making ? 'new-password' : 'current-password'}
        aria-describedby={making ? 'owner-password-rule' : undefined}
        value={password}
        onChange={(e) => {
          setPassword(e.target.value);
        }}
      />
      {making && (
        <p id="owner-password-rule" className="tiny" style={{ marginTop: 4 }}>
          {PASSWORD_RULE.charAt(0).toUpperCase() + PASSWORD_RULE.slice(1)}
        </p>
      )}

      {problem !== null && (
        <p className="sub" role="alert" style={{ marginTop: 10 }}>
          {problem}
        </p>
      )}
      {trouble !== null && <TroubleNote trouble={trouble} />}

      <button className="btn" type="submit" disabled={busy} style={{ marginTop: 16 }}>
        {making ? 'Make the account' : 'Sign in'}
      </button>
      <button
        className="btn ghost"
        type="button"
        style={{ marginTop: 8 }}
        onClick={() => {
          setMaking(!making);
          setProblem(null);
          setTrouble(null);
        }}
      >
        {making ? 'I already have an account' : 'Make a new account'}
      </button>
      {onLeave !== undefined && (
        <button
          className="btn ghost"
          type="button"
          style={{ marginTop: 8 }}
          onClick={onLeave}
        >
          Back
        </button>
      )}
    </form>
  );
}

// --- the family -------------------------------------------------------------

/**
 * Change your own password ([D-132](../../../docs/DECISIONS.md#d-132)).
 *
 * Folded away until asked for: it is the one thing on this screen nobody does
 * twice, and the screen's job is children and devices. The current password is
 * asked for because a signed-in laptop left open is fifteen minutes of anyone.
 */
function ChangePassword({ client }: { readonly client: Client }) {
  const [open, setOpen] = useState(false);
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [problem, setProblem] = useState<string | null>(null);
  const [trouble, setTrouble] = useState<Trouble | null>(null);
  const [done, setDone] = useState(false);
  const [busy, setBusy] = useState(false);

  if (!open) {
    return (
      <>
        {done && (
          <p className="sub" role="status" style={{ marginTop: 18 }}>
            Your password is changed. Use the new one next time.
          </p>
        )}
        <button
          className="btn ghost"
          type="button"
          style={{ marginTop: 18 }}
          onClick={() => {
            setOpen(true);
            setDone(false);
          }}
        >
          Change your password
        </button>
      </>
    );
  }

  async function submit(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    setProblem(null);
    setTrouble(null);
    try {
      await client.owner.changePassword.mutate({ current, next });
      setDone(true);
      setOpen(false);
      setCurrent('');
      setNext('');
    } catch (error) {
      const said = problemFrom(error);
      if (said === UNNAMED) setTrouble(troubleFrom(error));
      else setProblem(said);
    } finally {
      setBusy(false);
    }
  }

  return (
    <form style={{ marginTop: 18 }} onSubmit={(e) => void submit(e)}>
      <h3>Change your password</h3>
      <label
        className="tiny"
        htmlFor="current-password"
        style={{ display: 'block', marginTop: 8 }}
      >
        The one you use now
      </label>
      <input
        id="current-password"
        className="chip"
        style={{ width: '100%', marginTop: 4 }}
        type="password"
        autoComplete="current-password"
        value={current}
        onChange={(e) => {
          setCurrent(e.target.value);
        }}
      />
      <label
        className="tiny"
        htmlFor="new-password"
        style={{ display: 'block', marginTop: 10 }}
      >
        The new one — at least 12 characters
      </label>
      <input
        id="new-password"
        className="chip"
        style={{ width: '100%', marginTop: 4 }}
        type="password"
        autoComplete="new-password"
        value={next}
        onChange={(e) => {
          setNext(e.target.value);
        }}
      />
      {problem !== null && (
        <p className="sub" role="alert" style={{ marginTop: 10 }}>
          {problem}
        </p>
      )}
      {trouble !== null && <TroubleNote trouble={trouble} />}
      <button className="btn" type="submit" disabled={busy} style={{ marginTop: 12 }}>
        Change it
      </button>
    </form>
  );
}

function Family({
  client,
  onLeave,
}: {
  readonly client: Client;
  readonly onLeave: () => void;
}) {
  const [kids, setKids] = useState<Children>([]);
  const [gear, setGear] = useState<Devices>([]);
  /** Every language, and which each child is learning (D-189). */
  const [langs, setLangs] = useState<Languages | null>(null);
  const [name, setName] = useState('');
  const [problem, setProblem] = useState<string | null>(null);
  /** The child whose code is being set, and the digits so far. */
  const [choosing, setChoosing] = useState<{ learnerId: string; pin: string } | null>(
    null,
  );
  /** The child being deleted, and their name as typed back so far (D-186). */
  const [deleting, setDeleting] = useState<{ learnerId: string; typed: string } | null>(
    null,
  );
  /** "How it's going" open in place of this screen (D-194). */
  const [looking, setLooking] = useState(false);
  const loadDashboard = useCallback(
    (days: 7 | 30 | 90, learnerId: string | null) =>
      client.dashboard.family.query({ days, learnerId }),
    [client],
  );

  const refresh = useCallback(async () => {
    const [nextKids, nextGear] = await Promise.all([
      client.owner.children.query(),
      client.owner.devices.query(),
    ]);
    setKids(nextKids);
    setGear(nextGear);
    // On its own: a family screen that cannot say who learns what still has
    // to let a grown-up set a code or delete a profile.
    try {
      setLangs(await client.owner.languages.query());
    } catch {
      setLangs(null);
    }
  }, [client]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const run = (work: () => Promise<unknown>) => () => {
    setProblem(null);
    work()
      .then(() => refresh())
      .catch((error: unknown) => {
        setProblem(problemFrom(error));
      });
  };

  if (looking) {
    return (
      <Dashboard
        title="How it’s going"
        children={kids}
        load={loadDashboard}
        onClose={() => {
          setLooking(false);
        }}
      />
    );
  }

  return (
    <div className="grow pad">
      <p className="kicker">For grown-ups</p>
      <h2>Your family</h2>

      {kids.length > 0 && (
        <button
          className="btn navy"
          type="button"
          style={{ marginTop: 12 }}
          onClick={() => {
            setLooking(true);
          }}
        >
          How it’s going
        </button>
      )}

      {problem !== null && (
        <p className="sub" role="alert" style={{ marginTop: 10 }}>
          {problem}
        </p>
      )}

      <h3 style={{ marginTop: 18 }}>Children</h3>
      {kids.length === 0 && <p className="sub">No children yet.</p>}
      {kids.map((child, at) => (
        <div className="lrow" key={child.learnerId}>
          <div className="ic">
            <Animal
              species={child.avatarSpecies}
              background={child.avatarBackground}
              width={30}
              uid={`kid-${String(at)}`}
            />
          </div>
          <div className="tx" style={{ flex: 1 }}>
            {child.displayName}
            <div className="tiny">{child.hasPin ? 'Uses a code' : 'No code'}</div>
          </div>
          {child.hasPin ? (
            // A code defends against a sibling, not an attacker, so turning it
            // off is one tap and never a confirmation.
            <button
              className="chip"
              type="button"
              onClick={run(() =>
                client.owner.setPin.mutate({ learnerId: child.learnerId, pin: null }),
              )}
            >
              Remove the code
            </button>
          ) : (
            <button
              className="chip"
              type="button"
              onClick={() => {
                setProblem(null);
                setChoosing({ learnerId: child.learnerId, pin: '' });
              }}
            >
              Set a code
            </button>
          )}
          <button
            className="chip"
            type="button"
            aria-label={`Delete ${child.displayName}`}
            onClick={() => {
              setProblem(null);
              setChoosing(null);
              setDeleting({ learnerId: child.learnerId, typed: '' });
            }}
          >
            Delete
          </button>
        </div>
      ))}

      {deleting !== null &&
        (() => {
          const who = kids.find((c) => c.learnerId === deleting.learnerId);
          if (who === undefined) return null;
          // The one button in the app that cannot be undone, so the name is
          // typed back first. The server checks it again (D-186).
          const same =
            deleting.typed.trim().toLocaleLowerCase('da') ===
            who.displayName.trim().toLocaleLowerCase('da');
          return (
            <div
              className="card"
              role="alertdialog"
              aria-labelledby="delete-title"
              style={{ marginTop: 8 }}
            >
              <h3 id="delete-title">Delete {who.displayName}?</h3>
              <p className="tiny" style={{ marginTop: 4 }}>
                Everything {who.displayName} has done goes, for good: lessons, words,
                acorns, pets, badges and clothes. You can make a new profile with the
                same name afterwards, and it starts from the beginning.
              </p>
              <label
                className="tiny"
                htmlFor="delete-name"
                style={{ display: 'block', marginTop: 8 }}
              >
                Type {who.displayName} to be sure
              </label>
              <input
                id="delete-name"
                className="chip"
                style={{ width: '100%', marginTop: 6 }}
                autoComplete="off"
                value={deleting.typed}
                onChange={(e) => {
                  setDeleting({ ...deleting, typed: e.target.value });
                }}
              />
              <div className="row" style={{ marginTop: 8, gap: 8 }}>
                <button
                  className="btn"
                  type="button"
                  style={{
                    width: 'auto',
                    padding: '8px 14px',
                    background: 'var(--flame)',
                  }}
                  disabled={!same}
                  onClick={run(async () => {
                    await client.owner.deleteChild.mutate({
                      learnerId: deleting.learnerId,
                      confirm: deleting.typed,
                    });
                    setDeleting(null);
                  })}
                >
                  Delete {who.displayName} for good
                </button>
                <button
                  className="chip"
                  type="button"
                  onClick={() => {
                    setDeleting(null);
                  }}
                >
                  Cancel
                </button>
              </div>
            </div>
          );
        })()}

      {langs !== null && kids.length > 0 && (
        <div className="card" style={{ marginTop: 12 }}>
          <h3>Languages</h3>
          <p className="tiny" style={{ marginTop: 4 }}>
            What each child is learning. They can switch in their own settings too.
            Switching keeps their progress in every language.
            {langs.courses.length === 1 && ' English is the only language here so far.'}
          </p>
          {kids.map((child) => {
            const now =
              langs.children.find((c) => c.learnerId === child.learnerId)?.courseId ??
              '';
            return (
              <div className="lrow" key={`l-${child.learnerId}`}>
                <label
                  className="tx"
                  style={{ flex: 1 }}
                  htmlFor={`lang-${child.learnerId}`}
                >
                  {child.displayName}
                </label>
                <select
                  id={`lang-${child.learnerId}`}
                  className="chip"
                  value={now}
                  disabled={langs.courses.length < 2}
                  onChange={(event) => {
                    const courseId = event.target.value;
                    run(() =>
                      client.owner.setLanguage.mutate({
                        learnerId: child.learnerId,
                        courseId,
                      }),
                    )();
                  }}
                >
                  {langs.courses.map((course) => (
                    <option key={course.courseId} value={course.courseId}>
                      {flagFor(course.l2)} {course.title}
                    </option>
                  ))}
                </select>
              </div>
            );
          })}
        </div>
      )}

      {kids.length > 0 && (
        <div className="card" style={{ marginTop: 12 }}>
          <h3>What we keep track of</h3>
          <p className="tiny" style={{ marginTop: 4 }}>
            CapyTalk records which lessons get finished and which get left, so you can
            see what is working. It stays on this server and goes nowhere else. Your
            child can see everything you can.
          </p>
          {kids.map((child) => (
            <div className="lrow" key={`t-${child.learnerId}`}>
              <div className="tx" style={{ flex: 1 }}>
                {child.displayName}
                <div className="tiny">
                  {child.telemetryOff ? 'Not keeping track' : 'Keeping track'}
                </div>
              </div>
              {/*
                Off stops the writing, not a view. The server checks this before
                every event ([D-039](../../../docs/DECISIONS.md#d-039)), so a
                device that keeps sending is still not recorded.
              */}
              <button
                className="chip"
                type="button"
                aria-pressed={!child.telemetryOff}
                onClick={run(() =>
                  client.owner.setTelemetry.mutate({
                    learnerId: child.learnerId,
                    off: !child.telemetryOff,
                  }),
                )}
              >
                {child.telemetryOff ? 'Start again' : 'Stop'}
              </button>
            </div>
          ))}
        </div>
      )}

      {choosing !== null && (
        <div className="card" style={{ marginTop: 8 }}>
          <label className="tiny" htmlFor="pin" style={{ display: 'block' }}>
            Code for {kids.find((c) => c.learnerId === choosing.learnerId)?.displayName}{' '}
            (4 to 6 digits)
          </label>
          <div className="row" style={{ marginTop: 6, gap: 8 }}>
            <input
              id="pin"
              className="chip"
              style={{ flex: 1 }}
              // A numeric keypad on a phone, and still a text field, so leading
              // zeroes survive and the digits stay a string all the way down.
              inputMode="numeric"
              autoComplete="off"
              value={choosing.pin}
              onChange={(e) => {
                setChoosing({ ...choosing, pin: e.target.value });
              }}
            />
            <button
              className="btn"
              type="button"
              style={{ width: 'auto', padding: '8px 14px' }}
              onClick={run(async () => {
                await client.owner.setPin.mutate({
                  learnerId: choosing.learnerId,
                  pin: choosing.pin,
                });
                setChoosing(null);
              })}
            >
              Save
            </button>
            <button
              className="chip"
              type="button"
              onClick={() => {
                setChoosing(null);
              }}
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      <div className="row" style={{ marginTop: 12, gap: 8 }}>
        <input
          className="chip"
          style={{ flex: 1 }}
          aria-label="The child’s name"
          placeholder="Name"
          value={name}
          onChange={(e) => {
            setName(e.target.value);
          }}
        />
        <button
          className="btn"
          type="button"
          style={{ width: 'auto', padding: '8px 14px' }}
          disabled={name.trim() === ''}
          onClick={run(async () => {
            await client.owner.add.mutate({
              displayName: name.trim(),
              // Picked round-robin so a second child does not look like the
              // first. Changing it is the child's own job, later.
              avatarSpecies: SPECIES[kids.length % SPECIES.length] ?? 'fox',
              avatarBackground: '#eaf2ff',
            });
            setName('');
          })}
        >
          Add a child
        </button>
      </div>

      <h3 style={{ marginTop: 22 }}>Devices</h3>
      {gear.length === 0 && <p className="sub">No devices yet.</p>}
      {gear.map((device) => (
        <div className="lrow" key={device.deviceId}>
          <div className="ic" aria-hidden="true">
            📱
          </div>
          <div className="tx" style={{ flex: 1 }}>
            {device.label}
            <div className="tiny">
              {device.revokedAt !== null
                ? 'Blocked'
                : childCount(device.learnerIds.length)}
            </div>
          </div>
          {device.revokedAt === null && (
            <button
              className="chip"
              type="button"
              onClick={run(() =>
                client.owner.revokeDevice.mutate({ deviceId: device.deviceId }),
              )}
            >
              Block it
            </button>
          )}
        </div>
      ))}

      <p className="tiny" style={{ marginTop: 8 }}>
        To use CapyTalk on another tablet or computer, open it there and log in.
        Blocking one here signs it out.
      </p>

      {/*
        Below the devices, because it is a fact about *this* device rather than
        about a child ([D-096](../../../docs/DECISIONS.md#d-096)) — and not
        between "Children" and the box you add one in, which is where it first
        went and where it cut the only job this screen exists for in half.
      */}
      {(langs?.courses ?? [{ courseId: 'en.da', l2: 'en', title: 'English' }]).map(
        (course, _, all) => (
          <SpeechSetup
            key={course.courseId}
            l2={course.l2}
            title={course.title}
            named={all.length > 1}
          />
        ),
      )}

      <ChangePassword client={client} />

      <button
        className="btn ghost"
        type="button"
        style={{ marginTop: 18 }}
        onClick={onLeave}
      >
        Done
      </button>
    </div>
  );
}

export default function GrownUps({
  onLeave,
  build = createClient,
}: {
  readonly onLeave: () => void;
  /**
   * How to build the API client. Injected by tests; nothing else passes it.
   *
   * This screen builds **its own** client rather than taking the app's, so an
   * owner token can never end up on the connection a child's screens use. The
   * two kinds of token are already distinguishable to the server
   * ([D-067](../../../docs/DECISIONS.md#d-067)); keeping the clients apart means
   * one cannot be sent where the other was meant even by mistake.
   */
  readonly build?: (token: () => string | null) => Client;
}) {
  const [token, setToken] = useState<string | null>(null);
  // Read through a ref so the client is built once and still sees the token the
  // moment it arrives, without throwing away an in-flight request.
  const held = useRef<string | null>(null);
  held.current = token;
  const client = useMemo(() => build(() => held.current), [build]);

  return (
    <div className="frame">
      {token === null ? (
        <SignInOrUp client={client} onSignedIn={setToken} onLeave={onLeave} />
      ) : (
        <Family client={client} onLeave={onLeave} />
      )}
    </div>
  );
}
